import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiImage, FiX } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ManageRoomsServices from './Role-based/ManageRoomServices';
import { FaSearch } from 'react-icons/fa';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function ManageRoomItems() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    serviceid: '',
    name: '',
    baseprice: '',
    urgentprice: '',
    price_type: 'Per day',
    showenhancestay: false,
    refundable: false,
    value: '',
    value_type: 'P',
    is_multi: false,
    max_quantity: '',
    image: null,
    imagePreview: null,
  });
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);

  useEffect(() => {
    fetchData();
    fetchServices();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-items/`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-services/`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }
      const data = await response.json();
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to fetch services');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewItem((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewItem((prev) => ({
          ...prev,
          image: file,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setNewItem((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('refundable', newItem.refundable);
      formData.append('value', newItem.value ? parseFloat(newItem.value) : 0);
      formData.append('value_type', newItem.value_type);
      formData.append('is_multi', newItem.is_multi);
      formData.append('max_quantity', newItem.max_quantity ? parseInt(newItem.max_quantity) : 0);

      if (newItem.image) {
        formData.append('file', newItem.image);
      }

      // Create URL with query parameters
      const url = new URL(`${BQ_BASE_URL}/bq/api/room-service-items/`);
      url.searchParams.append('serviceid', parseInt(newItem.serviceid));
      url.searchParams.append('name', newItem.name);
      url.searchParams.append('baseprice', parseFloat(newItem.baseprice));
      url.searchParams.append('urgentprice', parseFloat(newItem.urgentprice));
      url.searchParams.append('showenhancestay', newItem.showenhancestay);
      url.searchParams.append('price_type', newItem.price_type);

      const response = await fetch(url.toString(), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to create item');
      }

      const result = await response.json();
      setItems((prev) => [...prev, result]);
      setIsModalOpen(false);
      setNewItem({
        serviceid: '',
        name: '',
        baseprice: '',
        urgentprice: '',
        price_type: 'Per day',
        showenhancestay: false,
        refundable: false,
        value: '',
        value_type: 'P',
        is_multi: false,
        max_quantity: '',
        image: null,
        imagePreview: null,
      });
      toast.success('Item created successfully!');
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error('Failed to create item');
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setIsConfirmDeleteOpen(true);
  };

  const handleDelete = async (itemId) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-items/${itemId}`, {
        method: 'DELETE',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete item');
      }

      setItems((prev) => prev.filter((item) => item.id !== itemId));
      toast.success('Item deleted successfully!');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // Group items by serviceid
  const itemsByService = services
    .map((service) => ({
      serviceName: service.name,
      items: filteredItems.filter((item) => item.serviceid === service.id),
    }))
    .filter((group) => group.items.length > 0);

  const handleEditClick = (item) => {
    setItemToEdit({
      ...item,
      price_type: item.price_type || 'Per day',
      image: null,
      imagePreview: item.imageurl || null,
      value: item.value || '',
      max_quantity: item.max_quantity || '',
    });
    setIsEditModalOpen(true);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setItemToEdit((prev) => ({
          ...prev,
          image: file,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeEditImage = () => {
    setItemToEdit((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('serviceid', parseInt(itemToEdit.serviceid));
      formData.append('name', itemToEdit.name);
      formData.append('baseprice', parseFloat(itemToEdit.baseprice));
      formData.append('urgentprice', parseFloat(itemToEdit.urgentprice));
      formData.append('showenhancestay', itemToEdit.showenhancestay);
      formData.append('price_type', itemToEdit.price_type || 'Per day');
      formData.append('refundable', itemToEdit.refundable || false);
      formData.append('value', itemToEdit.value ? parseFloat(itemToEdit.value) : 0);
      formData.append('value_type', itemToEdit.value_type || 'P');
      formData.append('is_multi', itemToEdit.is_multi || false);
      formData.append(
        'max_quantity',
        itemToEdit.max_quantity ? parseInt(itemToEdit.max_quantity) : 0
      );

      if (itemToEdit.image) {
        formData.append('file', itemToEdit.image);
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-items/${itemToEdit.id}`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to update item');
      }

      const result = await response.json();
      setItems((prev) => prev.map((item) => (item.id === result.id ? result : item)));
      setIsEditModalOpen(false);
      setItemToEdit(null);
      toast.success('Item updated successfully!');
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update item');
    }
  };

  return (
    <div className="max-w-8xl mx-auto p-6">
      <ManageRoomsServices />
      <ToastContainer position="top-right" autoClose={3000} />
      <h1 className="text-2xl font-semibold mt-12 mb-6">Room Service Items</h1>
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-1/3">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search items by name"
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <button
          className="bg-black text-white hover:text-white py-2 px-4 rounded-lg flex items-center gap-2"
          onClick={() => setIsModalOpen(true)}
        >
          <FiPlus className="h-5 w-5" /> Create New Item
        </button>
      </div>

      {/* Create Item Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Create Room Service Item</h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="mb-2">
                    <label
                      htmlFor="serviceid"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Service Sub-Category
                    </label>
                    <select
                      id="serviceid"
                      name="serviceid"
                      value={newItem.serviceid}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded text-sm"
                      required
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-2">
                    <label htmlFor="name" className="block text-gray-700 text-sm font-bold mb-1">
                      Item Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={newItem.name}
                      onChange={handleInputChange}
                      placeholder="Enter room service item here."
                      className="w-full p-2 border rounded text-sm"
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="baseprice"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Base Price
                    </label>
                    <input
                      type="number"
                      id="baseprice"
                      name="baseprice"
                      value={newItem.baseprice}
                      onChange={handleInputChange}
                      placeholder="Enter room service baseprice here."
                      className="w-full p-2 border rounded text-sm"
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="urgentprice"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Urgent Price
                    </label>
                    <input
                      type="number"
                      id="urgentprice"
                      name="urgentprice"
                      value={newItem.urgentprice}
                      onChange={handleInputChange}
                      placeholder="Enter room service urgentprice here."
                      className="w-full p-2 border rounded text-sm"
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="price_type"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Price Type
                    </label>
                    <select
                      id="price_type"
                      name="price_type"
                      value={newItem.price_type}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded text-sm"
                      required
                    >
                      <option value="Per item">Per Item</option>
                      <option value="Per person">Per Person</option>
                      <option value="Per session">Per Session</option>
                      <option value="Per day">Per Day</option>
                      <option value="Per service">Per Service</option>
                      <option value="Per night">Per Night</option>
                    </select>
                  </div>
                  <div className="mb-2">
                    <label htmlFor="value" className="block text-gray-700 text-sm font-bold mb-1">
                      Value
                    </label>
                    <input
                      type="number"
                      id="value"
                      name="value"
                      value={newItem.value}
                      onChange={handleInputChange}
                      placeholder="Enter value"
                      className="w-full p-2 border rounded text-sm"
                    />
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="value_type"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Value Type
                    </label>
                    <select
                      id="value_type"
                      name="value_type"
                      value={newItem.value_type}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded text-sm"
                    >
                      <option value="P">Percentage (P)</option>
                      <option value="F">Fixed (F)</option>
                    </select>
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="max_quantity"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Max Quantity
                    </label>
                    <input
                      type="number"
                      id="max_quantity"
                      name="max_quantity"
                      value={newItem.max_quantity}
                      onChange={handleInputChange}
                      placeholder="Enter max quantity"
                      className="w-full p-2 border rounded text-sm"
                    />
                  </div>
                </div>

                {/* Toggle Section */}
                <div className="space-y-4 mb-4 mt-4">
                  {/* Show in Enhance Stay Toggle */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-1">
                        Show in Enhance Stay
                      </label>
                      <p className="text-gray-500 text-xs">
                        Toggle this item should be visible to customers during reservation.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="showenhancestay"
                        checked={newItem.showenhancestay}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-checked:bg-black rounded-full transition-colors duration-300"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 transform peer-checked:translate-x-5"></div>
                    </label>
                  </div>

                  {/* Refundable Toggle */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-1">
                        Refundable
                      </label>
                      <p className="text-gray-500 text-xs">Toggle if this item is refundable</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="refundable"
                        checked={newItem.refundable}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-checked:bg-black rounded-full transition-colors duration-300"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 transform peer-checked:translate-x-5"></div>
                    </label>
                  </div>

                  {/* Is Multi Toggle */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-1">
                        Is Multi-Quantity
                      </label>
                      <p className="text-gray-500 text-xs">
                        Toggle if multiple quantities can be ordered
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_multi"
                        checked={newItem.is_multi}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-checked:bg-black rounded-full transition-colors duration-300"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 transform peer-checked:translate-x-5"></div>
                    </label>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-1">Item Image</label>
                  {newItem.imagePreview ? (
                    <div className="relative mb-2 inline-block">
                      <img
                        src={newItem.imagePreview}
                        alt="Preview"
                        className="h-20 w-24 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <FiX className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center px-3 py-2">
                        <FiImage className="w-5 h-5 mb-1 text-gray-500" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold text-sm">Click to upload</span> or drag and
                          drop
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG (MAX. 800x400px)</p>
                      </div>
                      <input
                        id="image-upload"
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                  )}
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-6 py-2 bg-red-500 text-sm text-white rounded hover:text-black transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-black text-sm text-white rounded hover:text-white transition duration-200"
                  >
                    Create Item
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {isEditModalOpen && itemToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Edit Room Service Item</h2>
              <form onSubmit={handleUpdate}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="mb-2">
                    <label
                      htmlFor="edit-serviceid"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Service Sub-Category
                    </label>
                    <select
                      id="edit-serviceid"
                      name="serviceid"
                      value={itemToEdit.serviceid}
                      onChange={(e) =>
                        setItemToEdit({
                          ...itemToEdit,
                          serviceid: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded text-sm"
                      required
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="edit-name"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Item Name
                    </label>
                    <input
                      type="text"
                      id="edit-name"
                      name="name"
                      value={itemToEdit.name}
                      onChange={(e) => setItemToEdit({ ...itemToEdit, name: e.target.value })}
                      className="w-full p-2 border rounded text-sm"
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="edit-baseprice"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Base Price
                    </label>
                    <input
                      type="number"
                      id="edit-baseprice"
                      name="baseprice"
                      value={itemToEdit.baseprice}
                      onChange={(e) =>
                        setItemToEdit({
                          ...itemToEdit,
                          baseprice: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded text-sm"
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="edit-urgentprice"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Urgent Price
                    </label>
                    <input
                      type="number"
                      id="edit-urgentprice"
                      name="urgentprice"
                      value={itemToEdit.urgentprice}
                      onChange={(e) =>
                        setItemToEdit({
                          ...itemToEdit,
                          urgentprice: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded text-sm"
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="edit-price_type"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Price Type
                    </label>
                    <select
                      id="edit-price_type"
                      name="price_type"
                      value={itemToEdit.price_type || 'Per day'}
                      onChange={(e) =>
                        setItemToEdit({
                          ...itemToEdit,
                          price_type: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded text-sm"
                      required
                    >
                      <option value="Per item">Per Item</option>
                      <option value="Per person">Per Person</option>
                      <option value="Per session">Per Session</option>
                      <option value="Per day">Per Day</option>
                      <option value="Per service">Per Service</option>
                      <option value="Per night">Per Night</option>
                    </select>
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="edit-value"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Value
                    </label>
                    <input
                      type="number"
                      id="edit-value"
                      name="value"
                      value={itemToEdit.value || ''}
                      onChange={(e) => setItemToEdit({ ...itemToEdit, value: e.target.value })}
                      placeholder="Enter value"
                      className="w-full p-2 border rounded text-sm"
                    />
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="edit-value_type"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Value Type
                    </label>
                    <select
                      id="edit-value_type"
                      name="value_type"
                      value={itemToEdit.value_type || 'P'}
                      onChange={(e) =>
                        setItemToEdit({
                          ...itemToEdit,
                          value_type: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded text-sm"
                    >
                      <option value="P">Percentage (P)</option>
                      <option value="F">Fixed (F)</option>
                    </select>
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="edit-max_quantity"
                      className="block text-gray-700 text-sm font-bold mb-1"
                    >
                      Max Quantity
                    </label>
                    <input
                      type="number"
                      id="edit-max_quantity"
                      name="max_quantity"
                      value={itemToEdit.max_quantity || ''}
                      onChange={(e) =>
                        setItemToEdit({
                          ...itemToEdit,
                          max_quantity: e.target.value,
                        })
                      }
                      placeholder="Enter max quantity"
                      className="w-full p-2 border rounded text-sm"
                    />
                  </div>
                </div>

                {/* Toggle Section for Edit Modal */}
                <div className="space-y-4 mb-4 mt-4">
                  {/* Show in Enhance Stay Toggle */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-1">
                        Show in Enhance Stay
                      </label>
                      <p className="text-gray-500 text-xs">
                        Toggle this item should be visible to customers during reservation.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="showenhancestay"
                        checked={itemToEdit.showenhancestay || false}
                        onChange={(e) =>
                          setItemToEdit({
                            ...itemToEdit,
                            showenhancestay: e.target.checked,
                          })
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-checked:bg-black rounded-full transition-colors duration-300"></div>
                      <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-300 transform peer-checked:translate-x-5"></div>
                    </label>
                  </div>

                  {/* Refundable Toggle */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-1">
                        Refundable
                      </label>
                      <p className="text-gray-500 text-xs">Toggle if this item is refundable</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="refundable"
                        checked={itemToEdit.refundable || false}
                        onChange={(e) =>
                          setItemToEdit({
                            ...itemToEdit,
                            refundable: e.target.checked,
                          })
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-checked:bg-black rounded-full transition-colors duration-300"></div>
                      <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-300 transform peer-checked:translate-x-5"></div>
                    </label>
                  </div>

                  {/* Is Multi Toggle */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-1">
                        Is Multi-Quantity
                      </label>
                      <p className="text-gray-500 text-xs">
                        Toggle if multiple quantities can be ordered
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_multi"
                        checked={itemToEdit.is_multi || false}
                        onChange={(e) =>
                          setItemToEdit({
                            ...itemToEdit,
                            is_multi: e.target.checked,
                          })
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-checked:bg-black rounded-full transition-colors duration-300"></div>
                      <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-300 transform peer-checked:translate-x-5"></div>
                    </label>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-1">Item Image</label>
                  {itemToEdit.imagePreview ? (
                    <div className="relative mb-2 inline-block">
                      <img
                        src={itemToEdit.imagePreview}
                        alt="Preview"
                        className="h-20 w-24 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={removeEditImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <FiX className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center px-3 py-2">
                        <FiImage className="w-6 h-6 mb-2 text-gray-500" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG (MAX. 800x400px)</p>
                      </div>
                      <input
                        id="edit-image-upload"
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleEditImageChange}
                      />
                    </label>
                  )}
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-6 py-2 text-sm bg-red-500 text-white rounded hover:text-black transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 text-sm bg-black text-white rounded hover:text-white transition duration-200"
                  >
                    Update Item
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Delete Modal */}
      {isConfirmDeleteOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
              <p>
                Are you sure you want to delete the item: <strong>{itemToDelete?.name}</strong>?
              </p>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setIsConfirmDeleteOpen(false)}
                  className="px-6 py-2 bg-black text-white rounded hover:text-white transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleDelete(itemToDelete.id);
                    setIsConfirmDeleteOpen(false);
                  }}
                  className="px-6 py-2 bg-red-500 text-white rounded hover:text-black transition duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Items Table */}
      <div className="space-y-8 mt-6">
        {itemsByService.length > 0 ? (
          itemsByService.map((serviceGroup) => (
            <div
              key={serviceGroup.serviceName}
              className="bg-white rounded-lg shadow overflow-hidden"
            >
              <div className="px-4 py-2 bg-white border-b">
                <h3 className="text-lg font-medium text-gray-900">{serviceGroup.serviceName}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Image
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Base Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Urgent Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Refundable
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value/Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Multi-Qty
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Max Qty
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Enhance Stay
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {serviceGroup.items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          {item.imageurl ? (
                            <img
                              src={item.imageurl}
                              alt={item.name}
                              className="h-12 w-12 object-cover rounded"
                            />
                          ) : (
                            <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center">
                              <FiImage className="text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{item.baseprice}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{item.urgentprice}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                            {item.price_type || 'Per day'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.refundable ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                              Yes
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.value
                            ? `${item.value}${item.value_type === 'P' ? '%' : ''}`
                            : 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.is_multi ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                              Yes
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.max_quantity || 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.showenhancestay ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                              Yes
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-4 justify-end">
                            <button
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                              onClick={() => handleEditClick(item)}
                            >
                              <FiEdit className="h-5 w-5" />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-900 transition-colors"
                              onClick={() => handleDeleteClick(item)}
                            >
                              <FiTrash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 text-center text-sm text-gray-500">
              No items found matching your search criteria
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ManageRoomItems;
